<template>
  <div class="asset-details-container">
    <div
      v-if="loading"
      class="text-center py-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">{{ $t('components.assetGroupDetails.loading') }}</span>
      </div>
    </div>

    <div
      v-else-if="error"
      class="alert alert-danger"
    >
      <i class="bi bi-exclamation-triangle" />
      {{ error }}
    </div>

    <div
      v-else-if="asset"
      class="asset-details-wrapper"
    >
      <!-- Hero Header -->
      <div class="asset-hero">
        <div class="hero-content">
          <div class="hero-left">
            <div class="asset-icon-wrapper">
              <i class="bi bi-hdd-rack-fill" />
            </div>
            <div class="hero-info">
              <h1 class="asset-title">
                {{ asset.name || $t('components.assetGroupDetails.unknownGroup') }}
              </h1>
              <div class="asset-meta">
                <span class="meta-item">
                  <i class="bi bi-upc-scan" />
                  {{ getGroupCode() }}
                </span>
                <span
                  class="meta-item"
                  v-if="asset.company"
                >
                  <i class="bi bi-building" />
                  {{ asset.company.name }}
                </span>
              </div>
            </div>
          </div>
          <div class="hero-right">
            <button
              class="btn-back"
              @click="goBack"
            >
              <i class="bi bi-arrow-left" />
              {{ $t('components.assetGroupDetails.backToList') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Modern Tabs -->
      <div class="modern-tabs">
        <button
          :class="{active: tabContent === TAB_DETAILS }"
          class="tab-btn"
          @click="setTab(TAB_DETAILS)"
        >
          <i class="bi bi-info-circle-fill" />
          <span>Szczegóły</span>
        </button>
        <button
          :class="{active: tabContent === TAB_HISTORY }"
          class="tab-btn"
          @click="setTab(TAB_HISTORY)"
        >
          <i class="bi bi-clock-history" />
          <span>Historia</span>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content-wrapper">
        <div
          v-if="tabContent === TAB_DETAILS"
          class="asset-details-content"
        >
          <div class="asset-overview-grid">
            <div class="asset-overview-chart">
              <template v-if="!isChartFullscreen">
                <div
                  v-if="chartHasData"
                  class="chart-container"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div class="chart-header">
                    <h3 class="chart-title">
                      <i class="bi bi-graph-up" />
                      <span>{{ getChartTitle() }}</span>
                    </h3>
                    <div class="chart-actions">
                      <button
                        class="btn btn-custom"
                        :disabled="!chartHasData"
                        @click="openChartFullscreen"
                        title="Pełny ekran"
                      >
                        <i class="bi bi-arrows-fullscreen" />
                      </button>
                      <button
                        class="btn btn-custom"
                        :disabled="!chartHasData"
                        @click="resetZoom('asset-chart')"
                        title="Reset zoom"
                      >
                        <i class="bi bi-arrow-clockwise" />
                      </button>
                      <button
                        class="btn btn-custom chart-action-export"
                        :disabled="!chartHasData"
                        @click="exportToPng()"
                        title="Eksport PNG"
                      >
                        <i class="bi bi-filetype-png" />
                      </button>
                      <button
                        class="btn btn-custom chart-action-export"
                        :disabled="!chartHasData"
                        @click="exportToJpg()"
                        title="Eksport JPG"
                      >
                        <i class="bi bi-filetype-jpg" />
                      </button>
                      <button
                        class="btn btn-custom chart-action-export"
                        :disabled="!chartHasData"
                        @click="exportCsv()"
                        title="Eksport CSV"
                      >
                        <i class="bi bi-filetype-csv" />
                      </button>
                    </div>
                  </div>
                  <div class="chart-body">
                    <LineChart
                      :key="chartRenderKey"
                      :height="chartHeight"
                      chart-id="asset-chart"
                      :chart-data="chartData"
                      :options="chartOptions"
                      :plugins="chartPlugins"
                    />
                  </div>
                </div>
                <div
                  v-else
                  class="chart-container chart-container-empty"
                  :class="{ 'dark-mode': isDarkMode }"
                >
                  <div
                    v-if="chartLoading"
                    class="chart-empty-state"
                  >
                    <div
                      class="spinner-border text-primary"
                      role="status"
                    >
                      <span class="visually-hidden">{{ $t('components.assetGroupDetails.loading') }}</span>
                    </div>
                  </div>
                  <div
                    v-else
                    class="chart-empty-state"
                  >
                    <i class="bi bi-graph-up" />
                    <div class="chart-empty-text">
                      {{ chartError || $t('components.assetGroupDetails.noHistory') }}
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div class="asset-overview-side">
              <div class="asset-tile asset-tile-image">
                <div class="asset-tile-header">
                  <div class="asset-tile-title">
                    <i class="bi bi-image" />
                    <span>{{ $t('components.assetGroupDetails.image') }}</span>
                  </div>
                  <button
                    class="asset-tile-action"
                    type="button"
                    :title="$t('components.assetGroupDetails.openImage')"
                    @click="openImageModal"
                  >
                    <i class="bi bi-arrows-fullscreen" />
                  </button>
                </div>
                <div
                  class="asset-tile-media"
                  @click="openImageModal"
                >
                  <img
                    :src="getAssetImageOrPlaceholder()"
                    :alt="asset.name || $t('components.assetGroupDetails.imageAlt')"
                    class="asset-tile-media-img"
                  >
                  <div class="asset-tile-media-overlay">
                    <i class="bi bi-arrows-fullscreen" />
                    <span>{{ $t('components.assetGroupDetails.imageHint') }}</span>
                  </div>
                </div>
              </div>

              <div class="asset-tile asset-tile-stats">
                <div class="asset-tile-header">
                  <div class="asset-tile-title">
                    <i class="bi bi-speedometer2" />
                    <span>{{ $t('components.assetGroupDetails.currentParameters') }}</span>
                  </div>
                </div>
                <div class="asset-stats-grid">
                  <template v-if="isDevice">
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-arrows-move" />
                        <span>{{ $t('components.assets.dataGrids.xAxis') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisValue('X_AXIS') }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-arrows-vertical" />
                        <span>{{ $t('components.assets.dataGrids.yAxis') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisValue('Y_AXIS') }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-arrows-collapse" />
                        <span>{{ $t('components.assets.dataGrids.zAxis') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisValue('Z_AXIS') }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-activity" />
                        <span>{{ $t('components.assets.dataGrids.magnitude') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisMagnitude() }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-activity" />
                        <span>{{ $t('components.assets.dataGrids.magnitudeNoGravity') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getAxisMagnitudeNoGravity() }}
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div
                      class="asset-stat"
                      :style="{ '--stat-accent': getTemperatureColor() }"
                    >
                      <div class="asset-stat-header">
                        <i class="bi bi-thermometer-half" />
                        <span>{{ $t('components.assets.dataGrids.temperature') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getTemperature() }}
                      </div>
                    </div>
                    <div
                      class="asset-stat"
                      :style="{ '--stat-accent': getHumidityColor() }"
                    >
                      <div class="asset-stat-header">
                        <i class="bi bi-droplet-half" />
                        <span>{{ $t('components.assets.dataGrids.humidity') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getHumidity() }}
                      </div>
                    </div>
                    <div class="asset-stat">
                      <div class="asset-stat-header">
                        <i class="bi bi-lightning-charge-fill" />
                        <span>{{ $t('components.assets.dataGrids.power') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getPowerSupply() }}
                      </div>
                    </div>
                    <div
                      class="asset-stat"
                      :class="getBatteryStatusClass()"
                    >
                      <div class="asset-stat-header">
                        <i class="bi bi-battery-charging" />
                        <span>{{ $t('components.assets.dataGrids.batteryStatus') }}</span>
                      </div>
                      <div class="asset-stat-value">
                        {{ getBatteryStatus() }}
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <div class="asset-tile asset-tile-info">
                <div class="asset-tile-header">
                  <div class="asset-tile-title">
                    <i class="bi bi-info-circle" />
                    <span>{{ $t('components.assetGroupDetails.basicInfo') }}</span>
                  </div>
                </div>
                <ul class="asset-info-list">
                  <li class="asset-info-item">
                    <span class="asset-info-label">{{ $t('components.assetGroupDetails.groupCodeLabel') }}</span>
                    <span class="asset-info-value">{{ getGroupCode() }}</span>
                  </li>
                  <li
                    v-if="asset.company"
                    class="asset-info-item"
                  >
                    <span class="asset-info-label">{{ $t('components.assets.dataGrids.company') }}</span>
                    <span class="asset-info-value">{{ asset.company.name }}</span>
                  </li>
                  <li class="asset-info-item">
                    <span class="asset-info-label">{{ $t('components.assetGroupDetails.lastActivityLabel') }}</span>
                    <span class="asset-info-value">{{ getLastActivity() }}</span>
                  </li>
                  <li
                    v-if="getLocationDescription() !== '—'"
                    class="asset-info-item"
                  >
                    <span class="asset-info-label">{{ $t('components.assetGroupDetails.locationLabel') }}</span>
                    <span class="asset-info-value">{{ getLocationDescription() }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div v-if="tabContent === TAB_HISTORY">
          <AssetGroupHistory
            :asset-id="asset.id"
          />
        </div>
      </div>
    </div>
  </div>

  <teleport to="body">
    <div
      v-if="isChartFullscreen"
      class="chart-fullscreen-overlay"
      :class="{ 'dark-mode': isDarkMode }"
      @click.self="closeChartFullscreen"
    >
      <div
        class="chart-container chart-container-fullscreen"
        :class="{ 'dark-mode': isDarkMode }"
      >
        <div class="chart-header">
          <h3 class="chart-title">
            <i class="bi bi-graph-up" />
            <span>{{ getChartTitle() }}</span>
          </h3>
          <div class="chart-actions">
            <button
              class="btn btn-custom"
              type="button"
              @click="closeChartFullscreen"
              title="Zamknij"
            >
              <i class="bi bi-x-lg" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="resetZoom('asset-chart')"
              title="Reset zoom"
            >
              <i class="bi bi-arrow-clockwise" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="exportToPng()"
              title="Eksport PNG"
            >
              <i class="bi bi-filetype-png" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="exportToJpg()"
              title="Eksport JPG"
            >
              <i class="bi bi-filetype-jpg" />
            </button>
            <button
              class="btn btn-custom"
              :disabled="!chartHasData"
              @click="exportCsv()"
              title="Eksport CSV"
            >
              <i class="bi bi-filetype-csv" />
            </button>
          </div>
        </div>
        <div
          v-if="chartHasData"
          class="chart-body"
        >
          <LineChart
            :key="chartRenderKey"
            :height="fullscreenChartHeight"
            chart-id="asset-chart"
            :chart-data="chartData"
            :options="chartOptions"
            :plugins="chartPlugins"
          />
        </div>
        <div
          v-else
          class="chart-body chart-fullscreen-empty"
        >
          <div
            v-if="chartLoading"
            class="chart-empty-state"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="visually-hidden">{{ $t('components.assetGroupDetails.loading') }}</span>
            </div>
          </div>
          <div
            v-else
            class="chart-empty-state"
          >
            <i class="bi bi-graph-up" />
            <div class="chart-empty-text">
              {{ chartError || $t('components.assetGroupDetails.noHistory') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { getHumanDateTime, getHumanDate, generateCsv } from '@/utils/tools'
import { getAssetGroup, getAssetGroupAverages, getAssetGroupHistoryRange } from '@/crud/asset-group'
import { LineChart } from 'vue-chart-3'
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js'
import 'chartjs-adapter-moment'
import zoomPlugin from 'chartjs-plugin-zoom'
import { resetZoom, exportToImg } from '@/utils/chart'
import AssetGroupHistory from '@/components/content/AssetGroupHistory.vue'
import { buildSeriesRecord, SERIES_META, type ChartSeriesRecord } from '@/utils/asset-history-series'

Chart.register(...registerables)
Chart.register(zoomPlugin)

const TAB_DETAILS = 0
const TAB_HISTORY = 1

const LATEST_PARAM = {
  MAC_ADDRESS: 'TL_EYE_MAC_ADDRESS',
  TEMPERATURE: 'TL_EYE_TEMPERATURE',
  HUMIDITY: 'TL_EYE_HUMIDITY',
  POWER_SUPPLY: 'TL_EYE_POWER_SUPPLY',
  BATTERY_VOLTAGE: 'TL_EYE_BATTERY_VOLTAGE',
  LOW_BATTERY: 'TL_EYE_LOW_BATTERY',
  MAGNITUDE: 'MAGNITUDE',
  MAGNITUDE_NO_GRAVITY: 'MAGNITUDE_NO_GRAVITY'
} as const

export default defineComponent({
  name: 'AssetGroupDetailsView',
  components: {
    AssetGroupHistory,
    LineChart
  },
  setup(props, context) {
    const i18n = useI18n()
    context.emit('pageName', i18n.t('components.assetGroupDetails.title'))

    return { i18n }
  },
  data() {
    return {
      TAB_DETAILS: TAB_DETAILS,
      TAB_HISTORY: TAB_HISTORY,
      tabContent: TAB_DETAILS,
      asset: null as any,
      isDevice: false,
      chartSeries: buildSeriesRecord({}, (key) => key) as ChartSeriesRecord,
      chartLoading: false,
      chartError: null as string | null,
      loading: true,
      error: null as string | null,
      isDarkMode: false,
      isChartFullscreen: false,
      fullscreenChartHeight: 720,
      bodyOverflowBeforeFullscreen: '' as string,
      chartHeight: 650,
      themeObserver: null as MutationObserver | null
    }
  },
  mounted() {
    this.fetchGroupDetails()
    this.checkDarkMode()
    this.updateChartHeight()
    window.addEventListener('resize', this.updateChartHeight)

    // Listen for theme changes
    this.themeObserver = new MutationObserver(() => {
      this.checkDarkMode()
    })
    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    })
  },
  unmounted() {
    // Clean up observer
    if (this.themeObserver) {
      this.themeObserver.disconnect()
    }
    this.closeChartFullscreen()
    window.removeEventListener('resize', this.updateChartHeight)
  },
  methods: {
    setTab(tab: number): void {
      this.tabContent = tab
    },
    getAssetImage() {
      if (!this.asset) return null
      return this.asset.imagePath ||
             this.asset.image_path ||
             this.asset.image ||
             this.asset.image_url ||
             this.asset.photo ||
             this.asset.photo_url ||
             this.asset.picture_url ||
             this.asset.picture ||
             this.asset.location?.imagePath ||
             this.asset.location?.image_path ||
             null
    },
    getAssetImageOrPlaceholder() {
      const assetImage = this.getAssetImage()
      if (assetImage) {
        if (typeof assetImage === 'string' && assetImage.includes('var/uploads/')) {
          return this.convertPathToUrl(assetImage)
        }
        return assetImage
      }

      return '/img/asset-placeholder.png'
    },
    convertPathToUrl(filePath: string): string {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    },
    openImageModal() {
      const imageUrl = this.getAssetImageOrPlaceholder()
      if (imageUrl) {
        window.open(imageUrl, '_blank')
      }
    },
    async fetchGroupDetails() {
      const groupId = Array.isArray(this.$route.params.id)
        ? this.$route.params.id[0]
        : this.$route.params.id

      if (!groupId) {
        this.error = this.$t('components.assetGroupDetails.noIdProvided')
        this.loading = false
        return
      }

      try {
        this.loading = true
        const [groupResponse, averagesResponse] = await Promise.all([
          getAssetGroup(groupId),
          getAssetGroupAverages(groupId)
        ])
        const groupPayload = groupResponse.data?.group || groupResponse.data?.data || groupResponse.data
        const averagesPayload = averagesResponse.data?.data || averagesResponse.data
        this.asset = {
          ...groupPayload,
          latest_params: this.buildGroupLatestParams(averagesPayload)
        }
        this.fetchChartSeries(groupId)
      } catch (err) {
        console.error('Error fetching group details:', err)
        this.error = this.$t('components.assetGroupDetails.errorLoading')
      } finally {
        this.loading = false
      }
    },
    buildGroupLatestParams(averages: any) {
      const params: any[] = []
      const temperature = averages?.temperatureAvg
      const humidity = averages?.humidityAvg
      const power = averages?.powerAvg

      if (temperature !== null && temperature !== undefined) {
        params.push({
          value: temperature,
          can_params_io: { param_name: LATEST_PARAM.TEMPERATURE }
        })
      }
      if (humidity !== null && humidity !== undefined) {
        params.push({
          value: humidity,
          can_params_io: { param_name: LATEST_PARAM.HUMIDITY }
        })
      }
      if (power !== null && power !== undefined) {
        params.push({
          value: power,
          can_params_io: { param_name: LATEST_PARAM.POWER_SUPPLY }
        })
      }

      return params
    },
    goBack() {
      this.$router.push('/assets/groups')
    },
    findLatestParam(paramName: string): any | null {
      const params = this.asset?.latest_params
      if (!Array.isArray(params) || !params.length) return null
      return params.find((param: any) => param?.can_params_io?.param_name === paramName) || null
    },
    getLatestParamNumber(paramName: string): number | null {
      const raw = this.findLatestParam(paramName)?.value
      const numeric = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN
      return Number.isFinite(numeric) ? numeric : null
    },
    getAxisValue(paramName: string) {
      const value = this.getLatestParamNumber(paramName)
      if (value === null) return '—'
      return `${value} mG`
    },
    getAxisMagnitudeValue() {
      return this.getLatestParamNumber(LATEST_PARAM.MAGNITUDE)
    },
    getAxisMagnitude() {
      const magnitude = this.getAxisMagnitudeValue()
      if (magnitude === null) return '—'
      return `${magnitude.toFixed(2)} mG`
    },
    getAxisMagnitudeNoGravity() {
      const magnitudeNoGravity = this.getLatestParamNumber(LATEST_PARAM.MAGNITUDE_NO_GRAVITY)
      if (magnitudeNoGravity === null) return '—'
      return `${magnitudeNoGravity.toFixed(2)} mG`
    },
    getChartTitle() {
      return this.$t('components.assetGroupDetails.chartTitle')
    },
    getGroupCode() {
      if (!this.asset) return '—'
      return this.asset.code || this.asset.id || '—'
    },
    getLocationDescription() {
      const location = this.asset?.location
      if (!location) return '—'
      const parts = [
        location.street,
        location.postal_code || location.postalCode,
        location.city,
        location.country
      ].filter((value: string | undefined) => !!value)

      return parts.length ? parts.join(', ') : '—'
    },
    getTemperature() {
      const value = this.getLatestParamNumber(LATEST_PARAM.TEMPERATURE)
      if (value === null) return '—'
      return `${value.toFixed(1)} °C`
    },
    getPowerSupply() {
      const milliVolts = this.getLatestParamNumber(LATEST_PARAM.POWER_SUPPLY) ??
        this.getLatestParamNumber(LATEST_PARAM.BATTERY_VOLTAGE)
      if (milliVolts === null) return '—'

      const volts = milliVolts / 1000
      return `${volts.toFixed(2)} V`
    },
    getHumidity() {
      const value = this.getLatestParamNumber(LATEST_PARAM.HUMIDITY)
      if (value === null) return '—'
      return `${value.toFixed(1)} %`
    },
    isBatteryOk(): boolean | null {
      const value = this.getLatestParamNumber(LATEST_PARAM.LOW_BATTERY)
      if (value === null) return null
      return Math.round(value) === 0
    },
    getBatteryStatusClass() {
      if (this.isBatteryOk() === null) return ''
      return this.isBatteryOk() ? 'status-ok' : 'status-bad'
    },
    getBatteryStatus() {
      const ok = this.isBatteryOk()
      if (ok === null) return '—'
      return ok ? this.$t('common.ok') : this.$t('common.bad')
    },
    isOnline(): boolean {
      const lastSeen = this.asset?.last_seen
      if (!lastSeen) return false
      const now = Math.floor(Date.now() / 1000)
      const diff = now - lastSeen
      return diff < 30 * 60
    },
    getOnlineStatus() {
      return this.isOnline() ? this.$t('common.online') : this.$t('common.offline')
    },
    getOnlineStatusClass() {
      return this.isOnline() ? 'online' : 'offline'
    },
    getTemperatureValue() {
      return this.getLatestParamNumber(LATEST_PARAM.TEMPERATURE) ?? 0
    },
    getHumidityValue() {
      return this.getLatestParamNumber(LATEST_PARAM.HUMIDITY) ?? 0
    },
    getLastActivity() {
      if (!this.asset || !this.asset.last_seen) return '—'
      return getHumanDateTime(this.asset.last_seen)
    },
    getTemperatureColor() {
      const temp = this.getTemperatureValue()
      if (temp < -10) return 'rgb(59, 130, 246)'
      if (temp >= -10 && temp < 20) return 'rgb(16, 185, 129)'
      if (temp >= 20 && temp < 30) return 'rgb(251, 146, 60)'
      return 'rgb(220, 38, 38)'
    },
    getHumidityColor() {
      const humidity = this.getHumidityValue()
      if (humidity < 39) return 'rgb(220, 38, 38)'
      if (humidity >= 39 && humidity <= 60) return 'rgb(16, 185, 129)'
      if (humidity > 60 && humidity <= 70) return '#252422'
      return 'rgb(124, 58, 237)'
    },
    checkDarkMode() {
      this.isDarkMode = document.body.classList.contains('dark-theme')
    },
    openChartFullscreen() {
      if (this.isChartFullscreen) return
      this.isChartFullscreen = true
      this.bodyOverflowBeforeFullscreen = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      this.updateFullscreenChartHeight()
      window.addEventListener('resize', this.updateFullscreenChartHeight)
      window.addEventListener('orientationchange', this.updateFullscreenChartHeight)
      document.addEventListener('keydown', this.onFullscreenKeydown)
    },
    closeChartFullscreen() {
      if (!this.isChartFullscreen) return
      this.isChartFullscreen = false
      document.body.style.overflow = this.bodyOverflowBeforeFullscreen || ''
      window.removeEventListener('resize', this.updateFullscreenChartHeight)
      window.removeEventListener('orientationchange', this.updateFullscreenChartHeight)
      document.removeEventListener('keydown', this.onFullscreenKeydown)
    },
    onFullscreenKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        this.closeChartFullscreen()
      }
    },
    updateFullscreenChartHeight() {
      const padding = 140
      this.fullscreenChartHeight = Math.max(320, window.innerHeight - padding)
    },
    updateChartHeight() {
      const width = window.innerWidth
      if (width <= 480) {
        this.chartHeight = 420
        return
      }
      if (width <= 768) {
        this.chartHeight = 420
        return
      }
      if (width <= 1024) {
        this.chartHeight = 520
        return
      }
      if (width <= 1600) {
        this.chartHeight = 650
        return
      }
      this.chartHeight = 720
    },
    resetZoom(chartId: string) {
      resetZoom(chartId)
    },
    exportToPng() {
      const groupCode = this.asset?.code || this.asset?.id || 'unknown'
      exportToImg('image/png', 'asset-chart', String(groupCode), this.isDarkMode)
    },
    exportToJpg() {
      const groupCode = this.asset?.code || this.asset?.id || 'unknown'
      exportToImg('image/jpeg', 'asset-chart', String(groupCode), this.isDarkMode)
    },
    exportCsv() {
      if (!this.chartHasData) return

      const exportRows: any[] = []
      Object.entries(this.chartSeries).forEach(([key, series]) => {
        if (!series.points.length) return
        series.points.forEach((point) => {
          exportRows.push({
            device_timestamp: Math.floor(point.x / 1000),
            series: key,
            param_name: series.paramName,
            label: series.label,
            value: point.y,
            unit: series.unit
          })
        })
      })

      if (!exportRows.length) return

      const filename = `group_history_${this.asset?.code || this.asset?.id || 'unknown'}_${getHumanDate(Date.now() / 1000)}`
      const locale = this.$i18n.locale
      generateCsv(exportRows, filename, this.$t, locale)
    },
    async fetchChartSeries(groupId: string | number) {
      this.chartLoading = true
      this.chartError = null
      this.chartSeries = buildSeriesRecord({}, (key) => key)

      try {
        const now = new Date()
        now.setSeconds(0, 0)
        const dateTo = now.toISOString()
        const dateFrom = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString()

        const response = await getAssetGroupHistoryRange(groupId, dateFrom, dateTo)
        this.chartSeries = buildSeriesRecord(
          response.data?.series,
          (key) => this.$t(key) as string
        )
      } catch (err) {
        console.error('Error fetching group chart history:', err)
        this.chartError = this.$t('components.assetGroupDetails.errorLoading') as string
      } finally {
        this.chartLoading = false
      }
    }
  },
  computed: {
    chartRenderKey(): string {
      return `${this.isDarkMode ? 'dark' : 'light'}-${this.isChartFullscreen ? 'fullscreen' : 'inline'}`
    },
    chartHasData(): boolean {
      return !!(this.chartSeries.temperature.points.length || this.chartSeries.humidity.points.length)
    },
    chartData(): ChartData<'line'> {
      const datasets: Array<any> = []

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        const series = this.chartSeries?.[key as keyof ChartSeriesRecord]
        if (series && series.points && series.points.length) {
          datasets.push({
            data: series.points,
            yAxisID: meta.axisId,
            label: this.$t(meta.labelKey),
            borderColor: meta.color,
            backgroundColor: meta.color,
            borderWidth: 2,
            tension: 0.1,
            spanGaps: true,
            hidden: key !== 'temperature' && key !== 'humidity',
            customKey: key
          })
        }
      })

      return { datasets }
    },
    chartOptions(): ChartOptions<'line'> {
      const vm = this
      const axes: Record<string, any> = {
        x: {
          type: 'time',
          time: {
            tooltipFormat: 'DD/MM HH:mm',
            displayFormats: {
              minute: 'DD/MM HH:mm',
              hour: 'DD/MM HH:mm',
              day: 'DD/MM'
            }
          },
          ticks: {
            color: this.isDarkMode ? 'rgba(255,255,255,0.7)' : undefined
          },
          grid: {
            color: this.isDarkMode ? 'rgba(255,255,255,0.05)' : undefined
          }
        }
      }

      Object.entries(SERIES_META).forEach(([key, meta]) => {
        if (axes[meta.axisId]) return
        if (key !== 'temperature' && key !== 'humidity') return

        const isHumidityAxis = meta.axisId === 'humidity'
        axes[meta.axisId] = {
          type: 'linear',
          position: meta.axisPosition,
          display: true,
          min: isHumidityAxis ? 0 : undefined,
          max: isHumidityAxis ? 100 : undefined,
          offset: meta.offset ?? false,
          title: {
            display: true,
            text: this.$t(meta.labelKey) as string,
            color: meta.color,
            font: { weight: 'bold' }
          },
          ticks: {
            color: meta.color,
            font: { weight: 'bold' },
            stepSize: isHumidityAxis ? 10 : undefined,
            callback: (value: string | number) => {
              const numeric = typeof value === 'string' ? parseFloat(value) : value
              if (!Number.isFinite(numeric)) {
                return ''
              }
              const rounded = Number(numeric.toFixed(2))
              return meta.tickFormatter ? meta.tickFormatter(rounded) : rounded
            }
          },
          grid: {
            drawOnChartArea: meta.axisId === 'temperature',
            color: vm.isDarkMode ? `${meta.color.replace('0.85', '0.2')}` : undefined
          }
        }
      })

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: axes,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: this.isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
              font: this.isDarkMode ? { size: 14, weight: 'bold' } : undefined
            }
          },
          title: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const dataset = context.dataset as any
                const label = context.dataset?.label ?? ''
                const rawValue = context.parsed.y
                const numeric = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue))
                const value = Number.isFinite(numeric) ? numeric.toFixed(2) : rawValue
                const unit = dataset?.customKey ? SERIES_META[dataset.customKey]?.unit || '' : ''
                return `${label}: ${value}${unit ? ' ' + unit : ''}`
              }
            }
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x'
            },
            zoom: {
              wheel: { enabled: true },
              pinch: { enabled: true },
              mode: 'x'
            }
          }
        },
        elements: {
          point: {
            radius: 0,
            hitRadius: 5
          }
        },
        layout: {
          padding: {
            left: 20,
            right: 20,
            top: 20,
            bottom: 10
          }
        }
      }
    },
    chartPlugins(): any[] {
      const bgColor = this.isDarkMode ? '#1e1e2d' : '#f5f8fa'
      return [
        {
          id: 'assetGroupHistoryBackground',
          beforeDraw: (chart: any) => {
            const ctx = chart.ctx
            ctx.save()
            ctx.fillStyle = bgColor
            ctx.fillRect(0, 0, chart.width, chart.height)
            ctx.restore()
          }
        }
      ]
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/asset-detail-view-style.scss';
</style>
